import styled from 'styled-components';

import colors from '../../../../../styles/colors';
import Button from '../../../../../components/Button';
import UserProfileCard from '../../../../components/UserProfileCard';

export const Wrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
`;

export const Header = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
`;

export const HeaderSubtitle = styled.div`
  font-size: 13px;
  color: ${colors.shades.gamma10};
  margin: 0 0 8px;
  line-height: 1.4;
`;

export const StateMessage = styled.div`
  color: ${colors.shades.gamma10};
  padding: 32px;
  text-align: center;
`;

export const CardWrapper = styled.div`
  border: 1px solid ${colors.shades.gamma1};
  border-left: 4px solid ${({ $blocked }) => ($blocked ? colors.shades.gamma8 : colors.base.primary)};
  border-radius: 8px;
  padding: 16px 20px;
  background: ${({ $blocked }) => ($blocked ? colors.shades.gamma5 : colors.base.alpha)};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const BlockedBanner = styled.div`
  font-size: 12px;
  color: ${colors.shades.gamma10};
  background: ${colors.shades.gamma1};
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const BlockedBannerTitle = styled.div`
  font-weight: 600;
  color: ${colors.base.beta};
`;

export const BlockedBannerIds = styled.div`
  font-family: ${({ theme }) => theme?.fonts?.mono || 'ui-monospace, SFMono-Regular, Menlo, monospace'};
  font-size: 11px;
  word-break: break-all;
`;

export const StyledUserProfileCard = styled(UserProfileCard)`
  max-width: fit-content;
  a {
    text-decoration: none;
  }
`;

export const ConflictBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid ${colors.shades.gamma4};

  &:first-of-type {
    border-top: none;
    padding-top: 0;
  }
`;

export const BoxRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 12px;
`;

export const BoxBase = styled.div`
  flex: 1;
  padding: 10px 12px;
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const RestrictedBox = styled(BoxBase)`
  background: ${colors.shades.gamma4};
  border: 1px solid ${colors.shades.gamma1};
`;

const replacementBoxColors = ({ $picked, $duplicate }) => {
  if ($duplicate) return { background: colors.shades.warning1, border: colors.shades.warning3 };
  if ($picked) return { background: colors.shades.success5, border: colors.shades.success3 };
  return { background: colors.shades.gamma4, border: colors.shades.gamma1 };
};

export const ReplacementBox = styled(BoxBase)`
  background: ${(props) => replacementBoxColors(props).background};
  border: 1px solid ${(props) => replacementBoxColors(props).border};
`;

export const BoxLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  color: ${({ $accent }) => ($accent ? colors.shades.success7 : colors.shades.gamma10)};
`;

export const BoxValue = styled.div`
  font-size: 14px;
  font-weight: 500;
`;

export const ArrowGlyph = styled.div`
  align-self: center;
  font-size: 18px;
  color: ${colors.shades.gamma10};
`;

export const MatchInfo = styled.div`
  font-size: 12px;
  color: ${colors.shades.gamma10};
  background: ${colors.shades.alpha2};
  padding: 8px 12px;
  border-radius: 6px;
`;

export const MatchInfoText = styled.span``;

export const MatchInfoQuote = styled.strong`
  color: ${colors.base.beta};
`;

export const ScopeInfo = styled.div`
  font-size: 12px;
  color: ${colors.shades.gamma10};
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
  margin-top: 4px;
`;

export const ActionButton = styled(Button)`
  padding: 8px 16px;
  border-radius: 6px;
  width: unset;
  font-size: 13px;
  line-height: 1.4;
  font-weight: 600;
`;

export const PickerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid ${({ $picked }) => ($picked ? colors.shades.success3 : colors.shades.gamma1)};
  background: ${colors.base.alpha};
  color: ${({ $picked }) => ($picked ? colors.base.beta : colors.shades.gamma10)};
  font-size: 13px;
  text-align: left;
  cursor: pointer;
  min-height: 36px;

  &::after {
    content: '▾';
    margin-left: 8px;
    color: ${colors.shades.gamma10};
  }

  &:hover {
    border-color: ${({ $picked }) => ($picked ? colors.shades.success3 : colors.shades.gamma3)};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const SuggestedTag = styled.span`
  align-self: flex-start;
  margin-top: 4px;
  font-size: 11px;
  font-style: italic;
  color: ${colors.shades.success7};
`;

export const DuplicateTag = styled(SuggestedTag)`
  font-weight: 600;
  color: ${colors.base.beta};
`;

export const DuplicateSummary = styled.div`
  flex-basis: 100%;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.base.beta};
  background: ${colors.shades.warning1};
  border: 1px solid ${colors.shades.warning3};
  border-radius: 6px;
  padding: 8px 12px;
`;

export const ExerciseChangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 20px;
`;

export const ExerciseChangeTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
`;
